/**
 * Client for the platform-admin runtime GIT browser
 * (`GET /api/platform/runtimes/:userId/git/{log,commit,blob}`).
 *
 * Sibling of [fs-browser-api.ts](./fs-browser-api.ts) and deliberately
 * shaped the same way: those endpoints PROXY the runtime plugin's raw
 * response, which is NOT wrapped in lexserver's `{ok,data}` envelope, so
 * `PlatformApi.request()`'s envelope-unwrap would reject it. This thin
 * client returns the body verbatim. Auth uses the same cached Lexiun
 * (platform-admin) JWT + W3C traceparent as PlatformApi.
 *
 * Data path (3-lane build, thread `…-operator-facing-git-re`):
 *   LexPortal → lexserver `/api/platform/runtimes/:userId/git/*`
 *   (passthrough proxy, `aff0c5e92`) → runtime `/api/lx/git/*`
 *   (lexiun-core, `5fd262b3d`) → baked `lx-git` verb → the case's `.git`.
 *
 * The FROZEN contract below is git-implementation's, verbatim — do not
 * "tidy" the field names. Three deviations from the original sketch are
 * deliberate and load-bearing:
 *   - `status` (not `type`) on a commit file row;
 *   - `parentSha` is explicitly `null` on a root commit (never absent),
 *     so "root commit" is distinguishable from "field missing";
 *   - `encoding` is always `"utf8"`; a BINARY blob comes back as
 *     `{binary:true, content:null}` — never base64. Render "binary file
 *     — not viewable" rather than attempting to decode.
 *
 * ⚠ There is NO unified diff anywhere in this API: the baked verb
 * allow-lists no `-p`/`-U`. A diff view is assembled client-side from
 * `commit` (name-status ⋈ numstat) + TWO `blob` reads. See
 * `git-diff.ts` for that assembly.
 */

import { getLexiunToken } from "./lexiun-jwt";
import { newTraceparent } from "./traceparent";

const LEXSERVER_BASE = "/lexserver";

/**
 * The trigger class of a checkpoint commit. **TWO vocabularies share this
 * field** (confirmed by git-implementation 2026-07-27, spec §6.4-bis):
 *
 *  1. the QUALIFIED `Lex-Trigger` trailer — the normal case:
 *     `leftover-pre-run` · `run-terminal` · `run-park` · `orphan-sweep` · `manual`
 *  2. the UNQUALIFIED commit CLASS, emitted as a fallback when a commit
 *     carries no trailer: `run` · `leftover` · `orphan-sweep` · `manual`
 *
 * ⚠ NOTHING may compare a trigger with `===` — use `classifyTrigger()`.
 * Two vocabularies plus future qualifiers make exact matching a silent
 * failure (it greys out the commonest triggers rather than erroring).
 * `run-park` in particular appears only when a PARKED (`awaiting_user`)
 * mission checkpoints, so it is easy to miss in local data.
 *
 * NOT in this union, deliberately: `delete` and `note-backfill` are not
 * triggers at all (an `lx_delete` checkpoint takes the ordinary C1 path
 * ⇒ `leftover-pre-run`; `note-backfill` is a *summary* class). `restore`
 * is listed because it arrives with v1.1 `lx_case_restore` — it will not
 * appear on the wire before then.
 */
export type LexTrigger =
  | "run"
  | "run-terminal"
  | "run-park"
  | "leftover"
  | "leftover-pre-run"
  | "orphan-sweep"
  | "manual"
  | "restore";

/** Coarse family a `Lex-Trigger` belongs to, for colour/iconography. */
export type TriggerFamily = "run" | "leftover" | "sweep" | "delete" | "restore" | "other";

/**
 * Bucket a raw trigger into a display family, tolerant of BOTH wire
 * vocabularies (qualified trailer + unqualified commit class) and of
 * future qualifiers.
 *
 * ORDER IS LOAD-BEARING: `leftover` is tested BEFORE any bare `run`
 * test, because `leftover-pre-run` ends in "run" and a substring match
 * would misfile it as a run. `run` is therefore matched by PREFIX only —
 * which also makes `run-park` (parked-mission checkpoints, rare in local
 * data) fall out correctly without a dedicated branch.
 *
 * The `delete` branch is DEFENSIVE, not currently reachable: there is no
 * `delete` trigger today (an `lx_delete` checkpoint commits as
 * `leftover-pre-run`). It is kept so that if such a value is ever
 * introduced it renders loud rather than silently neutral. `restore`
 * becomes reachable with v1.1 `lx_case_restore`.
 */
export function classifyTrigger(trigger: string): TriggerFamily {
  const t = trigger.toLowerCase();
  if (t.includes("delete")) return "delete";
  if (t.includes("restore")) return "restore";
  if (t.startsWith("leftover")) return "leftover";
  if (t.startsWith("run")) return "run";
  if (t.includes("sweep")) return "sweep";
  return "other";
}

/** One row of `git log`. */
export interface GitCommitRow {
  /** 7 chars — matches the `/checkpoints` summary SHA suffix, so a row
   *  there can deep-link straight into this browser. */
  sha7: string;
  /** Full 40 — pass this to `commit` / `blob`. */
  sha: string;
  subject: string;
  lexTrigger: LexTrigger | string;
  /** ISO-8601 (`%cI`). */
  authorDate: string;
  /** ⚠ Counts PATHS, and a rename contributes BOTH sides (an R-only
   *  commit reports 2). For an exact per-file count use
   *  `GitCommitDetail.files.length`. */
  filesChanged: number;
  /** The LLM checkpoint description from `refs/notes/lexiun`. OMITTED
   *  when empty — not empty-string. */
  note?: string;
}

export interface GitLogResult {
  caseId: string;
  commits: GitCommitRow[];
  /** Skip-offset for the next page; `null` on the last page. */
  nextCursor: number | null;
}

/** `--name-status` change type. A/M/D/R dominate; C/T are possible. */
export type GitFileStatus = "A" | "M" | "D" | "R" | "C" | "T";

/** One changed path in a commit — name-status joined with numstat. */
export interface GitCommitFile {
  status: GitFileStatus | string;
  /** C-quote DECODED server-side (accented pt-BR filenames render
   *  correctly, not as mojibake). */
  path: string;
  /** Only present for R (rename) / C (copy). */
  oldPath?: string;
  /** `null` = binary or unmatched, NOT zero. */
  added: number | null;
  removed: number | null;
  binary: boolean;
}

export interface GitCommitDetail {
  caseId: string;
  sha: string;
  /** ALWAYS present; `null` means this is the root commit (no parent to
   *  diff against — render the "added" side only). */
  parentSha: string | null;
  files: GitCommitFile[];
}

export interface GitBlobResult {
  caseId: string;
  sha: string;
  path: string;
  /** `null` when `binary` is true. */
  content: string | null;
  /** Always `"utf8"` — the API never returns base64. */
  encoding: "utf8";
  binary: boolean;
  /** Content is capped at 256 KB server-side. */
  truncated: boolean;
  size: number;
}

/** Error codes the git surface returns in `{error, message?}`. Kept as a
 *  union for exhaustive UI handling, widened to `string` so an unknown
 *  future code degrades to a generic message instead of a type error. */
export type GitBrowserErrorCode =
  | "CASE_ID_REQUIRED"
  | "SHA_REQUIRED"
  | "PATH_REQUIRED"
  | "SHA_UNSAFE"
  | "PATH_UNSAFE"
  | "MISSING_BEARER_TOKEN"
  | "INVALID_TOKEN"
  | "PLATFORM_ADMIN_REQUIRED"
  | "CASE_NOT_FOUND"
  | "COMMIT_NOT_FOUND"
  | "PATH_NOT_FOUND"
  | "METHOD_NOT_ALLOWED"
  | "GIT_ERROR"
  | "RUNTIME_NOT_FOUND"
  | string;

export class GitBrowserError extends Error {
  constructor(
    public code: GitBrowserErrorCode,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "GitBrowserError";
  }
}

/**
 * Operator-facing sentence for a git-browser failure. The raw codes are
 * accurate but terse; a platform-admin staring at `CASE_NOT_FOUND`
 * should be told it means "not on THIS runtime" (the case may exist
 * elsewhere in the fleet — a real and confusing case when the wrong
 * runtime is connected).
 */
export function describeGitError(e: GitBrowserError): string {
  switch (e.code) {
    case "CASE_NOT_FOUND":
      return "That case has no repository on this runtime. Check that the correct runtime is connected.";
    case "COMMIT_NOT_FOUND":
      return "That commit is not in this case's repository.";
    case "PATH_NOT_FOUND":
      return "That file does not exist at this commit.";
    case "PLATFORM_ADMIN_REQUIRED":
      return "Platform-admin scope is required to read a case repository.";
    case "RUNTIME_NOT_FOUND":
      return "The selected runtime is not reachable.";
    case "GIT_ERROR":
      return `Git failed on the runtime: ${e.message}`;
    default:
      return e.message || e.code;
  }
}

async function gitRequest<T>(path: string): Promise<T> {
  const token = await getLexiunToken();
  const headers: Record<string, string> = { traceparent: newTraceparent() };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${LEXSERVER_BASE}${path}`, { headers });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new GitBrowserError(
      body.error ?? "GIT_ERROR",
      body.message ?? res.statusText,
      res.status,
    );
  }
  return res.json() as Promise<T>;
}

function base(userId: string): string {
  return `/api/platform/runtimes/${encodeURIComponent(userId)}/git`;
}

/** Commit log for a case repo, newest first. An EMPTY repo is a
 *  successful `{commits: [], nextCursor: null}` — not an error. */
export function getGitLog(
  userId: string,
  caseId: string,
  opts: { limit?: number; cursor?: number } = {},
): Promise<GitLogResult> {
  const qs = new URLSearchParams({ caseId });
  // Server caps limit at 200 and falls back to defaults on garbage —
  // we still send only what the caller asked for.
  if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
  if (opts.cursor !== undefined) qs.set("cursor", String(opts.cursor));
  return gitRequest<GitLogResult>(`${base(userId)}/log?${qs}`);
}

/** Changed-path list for one commit (name-status ⋈ numstat). */
export function getGitCommit(
  userId: string,
  caseId: string,
  sha: string,
): Promise<GitCommitDetail> {
  const qs = new URLSearchParams({ caseId, sha });
  return gitRequest<GitCommitDetail>(`${base(userId)}/commit?${qs}`);
}

/** A file's content AS OF a commit (`git show <sha>:<path>`). */
export function getGitBlob(
  userId: string,
  caseId: string,
  sha: string,
  path: string,
): Promise<GitBlobResult> {
  const qs = new URLSearchParams({ caseId, sha, path });
  return gitRequest<GitBlobResult>(`${base(userId)}/blob?${qs}`);
}
